import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    
    // Add dropdown arrows
    setTimeout(() => this.#addDropdownArrows(), 300);
    
    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #state = { activeItem: null };

  /**
   * ADDED: Method to add permanent dropdown arrows
   */
  #addDropdownArrows() {
    const menuItems = this.querySelectorAll('.menu-list__list-item');
    
    menuItems.forEach(item => {
      const hasSubmenu = item.querySelector('.menu-list__submenu') || 
                        item.querySelector('[data-menu-list-id="MegaMenuList-1"]');
      
      if (hasSubmenu) {
        const link = item.querySelector('.menu-list__link');
        if (link && !link.querySelector('.dropdown-arrow')) {
          // Create permanent arrow
          const arrow = document.createElement('span');
          arrow.className = 'dropdown-arrow';
          arrow.innerHTML = '▾';
          arrow.style.cssText = `
            display: inline-block;
            margin-left: 6px;
            font-size: 12px;
            color: #666;
            transition: transform 0.3s ease;
            opacity: 1 !important;
            visibility: visible !important;
          `;
          
          link.appendChild(arrow);
          
          // Permanent listeners
          item.addEventListener('mouseenter', () => {
            arrow.style.transform = 'rotate(180deg)';
            arrow.style.color = '#2563eb';
          });
          
          item.addEventListener('mouseleave', () => {
            // Only reset if this is not the active item
            if (this.#state.activeItem !== item) {
              arrow.style.transform = 'rotate(0deg)';
              arrow.style.color = '#666';
            }
          });
        }
      }
    });
  }

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedActivateHandler(event);
  };

  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;
    let item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const prevArrow = previouslyActiveItem.querySelector('.dropdown-arrow');
      if (prevArrow) {
        prevArrow.style.transform = 'rotate(0deg)';
        prevArrow.style.color = '#666';
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Update arrow for active item
    const arrow = item.querySelector('.dropdown-arrow');
    if (arrow) {
      arrow.style.transform = 'rotate(180deg)';
      arrow.style.color = '#2563eb';
    }

    let submenu = findSubmenu(item);
    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  deactivate(event) {
    this.#debouncedActivateHandler.cancel();
    if (!(event.target instanceof Element)) return;
    const item = findMenuItem(event.target);
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);
    const arrow = item.querySelector('.dropdown-arrow');
    if (arrow) {
      arrow.style.transform = 'rotate(0deg)';
      arrow.style.color = '#666';
    }

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      if (submenu) delete submenu.dataset.active;
    }, Math.max(0, this.animationDelay - 150));
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}